import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { OnChangeFn, OnTouchedFn, provideCva } from '../cva.utils';

export type RadioSize = 'sm' | 'md';

@Component({
  selector: 'app-radio',
  standalone: true,
  templateUrl: './radio.component.html',
  styleUrls: ['./radio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideCva(RadioComponent)],
  host: {
    '[class.app-radio-disabled]': 'isDisabled()',
    '[class.app-radio-checked]': 'isChecked()',
    '[class.app-radio-readonly]': 'readonly()',
    '[class.app-radio-size-md]': 'size() === "md"',
    '[class.app-radio-has-label]': '!!label()',
    '(click)': 'onHostClick($event)',
  },
})
export class RadioComponent implements ControlValueAccessor {
  /** Value this option represents in the radio group. */
  readonly value = input.required<string>();
  readonly label = input<string>('');
  readonly name = input<string>('');
  readonly inputId = input<string>('');
  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly size = input<RadioSize>('sm');

  /**
   * Optional controlled checked state (when not using ngModel/CVA).
   * Prefer binding the group value via ngModel on each `app-radio`.
   */
  readonly checked = input<boolean | undefined>(undefined);

  readonly selectedChange = output<string>();

  private readonly cvaValue = signal<string | null>(null);
  private readonly disabledFromForm = signal(false);

  readonly isChecked = computed(() => {
    const controlled = this.checked();
    if (controlled !== undefined) {
      return controlled;
    }
    return this.cvaValue() === this.value();
  });

  readonly isDisabled = computed(() => this.disabled() || this.disabledFromForm());

  private onChange: OnChangeFn<string | null> = () => {};
  private onTouched: OnTouchedFn = () => {};

  writeValue(value: string | null): void {
    this.cvaValue.set(value ?? null);
  }

  registerOnChange(fn: OnChangeFn<string | null>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledFromForm.set(isDisabled);
  }

  onHostClick(event: Event): void {
    if (this.isDisabled() || this.readonly()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.select();
  }

  private select(): void {
    const next = this.value();
    this.cvaValue.set(next);
    this.onChange(next);
    this.onTouched();
    this.selectedChange.emit(next);
  }
}
